import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ReturnForm } from "./ReturnForm";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import TableSkeleton from "../TableSkeleton";

const EditReturn = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();

  const [billData, setBillData] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [medicineName, setMedicineName] = useState(""); 
  const [quantity, setQuantity] = useState(0); 
  const [returnQuantities, setReturnQuantities] = useState({});
  const [adjustmentType, setAdjustmentType] = useState("stock"); 
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(true); 
  const [updatedAt, setUpdatedAt] = useState(null);

  const [returnDate, setReturnDate] = useState(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  });

  const isWastage = type === "Wastage"; // Use 'Wastage' as string literal
  const isCustomerReturn = type === "CustomerReturn";
  const isSupplierReturn = type === "SupplierReturn";
  
  const API_KEY =
    process.env.REACT_APP_API_KEY ||
    "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
  const API_BASE_URL = "https://api.muktihospital.com"; // Your API Base URL

  // Determine endpoint and navigateTo based on type
  const endpoint = isWastage ? `Wastage/${id}` : `${type}/${id}`; 
  const navigateTo = isWastage ? "/wastage-returns" : (isCustomerReturn ? "/invoice-return-list" : "/supplier-return-list"); 

  const getLocalDateString = useCallback(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  const getLocalDateTimeISO = useCallback((dateString = null) => {
    let date;

    if (dateString) {
      if (dateString.includes("T") || dateString.includes("Z")) {
        date = new Date(dateString);
      } else {
        const [year, month, day] = dateString.split("-").map(Number);
        date = new Date(year, month - 1, day);
      }
    } else {
      date = new Date();
    }

    if (isNaN(date.getTime())) {
      console.error("Invalid date passed to getLocalDateTimeISO:", dateString);
      return null;
    }

    const userOffsetMinutes = date.getTimezoneOffset();
    const userOffsetMs = userOffsetMinutes * 60 * 1000;
    const dateInUTC = date.getTime() + userOffsetMs;

    const targetOffsetHours = 6;
    const targetOffsetMs = targetOffsetHours * 60 * 60 * 1000;

    const finalDate = new Date(dateInUTC + targetOffsetMs);

    const year = finalDate.getFullYear();
    const month = String(finalDate.getMonth() + 1).padStart(2, "0");
    const day = String(finalDate.getDate()).padStart(2, "0");
    const hours = String(finalDate.getHours()).padStart(2, "0");
    const minutes = String(finalDate.getMinutes()).padStart(2, "0");
    const seconds = String(finalDate.getSeconds()).padStart(2, "0");
    const milliseconds = String(finalDate.getMilliseconds()).padStart(3, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}+06:00`;
  }, []);

  const normalizeDateToLocalDay = useCallback((dateInput) => {
    if (!dateInput) return null;

    let date;
    if (dateInput instanceof Date) {
      date = dateInput;
    } else if (typeof dateInput === "string") {
      if (dateInput.includes("T") || dateInput.includes("Z")) {
        date = new Date(dateInput);
      } else {
        const [year, month, day] = dateInput.split("-").map(Number);
        date = new Date(year, month - 1, day);
      }
    } else {
      console.warn(
        "normalizeDateToLocalDay received unsupported dateInput type:",
        typeof dateInput,
        dateInput
      );
      return null;
    }

    if (isNaN(date.getTime())) {
      console.error("normalizeDateToLocalDay failed to parse date:", dateInput);
      return null;
    }

    const localYear = date.getFullYear();
    const localMonth = date.getMonth();
    const localDay = date.getDate();

    // Create a new Date object at local midnight
    return new Date(localYear, localMonth, localDay);
  }, []);

  const compareDatesLocal = useCallback(
    (date1, date2) => {
      const d1 = normalizeDateToLocalDay(date1);
      const d2 = normalizeDateToLocalDay(date2);

      if (!d1 || !d2) return null;

      return {
        isSame: d1.getTime() === d2.getTime(),
        isBefore: d1.getTime() < d2.getTime(),
        isAfter: d1.getTime() > d2.getTime(),
        d1Local: d1,
        d2Local: d2,
      };
    },
    [normalizeDateToLocalDay]
  );

  const validateReturnDateLocal = useCallback(
    (returnDateInput, originalDateInput, context = "") => {
      if (!returnDateInput || !originalDateInput) return true;

      try {
        const normalizedReturnDate = normalizeDateToLocalDay(returnDateInput);
        const normalizedOriginalDate = normalizeDateToLocalDay(originalDateInput);

        if (!normalizedReturnDate || !normalizedOriginalDate) {
          throw new Error("তারিখ পার্স করতে সমস্যা হয়েছে।");
        }

        // The only invalid case is if return date is strictly BEFORE original date
        // Allow return on the same day as the original bill/purchase date.
        if (normalizedReturnDate < normalizedOriginalDate) {
          throw new Error(`রিটার্ন তারিখ ${context} তারিখের আগে হতে পারে না`);
        }

        return true; 
      } catch (error) {
        console.error("Local date validation error:", error);
        if (error.message.includes("রিটার্ন তারিখ")) {
          throw error;
        }
        return true;
      }
    },
    [normalizeDateToLocalDay] // Removed compareDatesLocal from dependencies as it's not used directly here
  );

  const isItemExpired = useCallback(
    (item) => {
      if (!item?.expiryDate) return false;
      const currentDate = new Date();
      const expirationDate = new Date(item.expiryDate);
      return normalizeDateToLocalDay(expirationDate) < normalizeDateToLocalDay(currentDate);
    },
    [normalizeDateToLocalDay]
  );

  const hasValidReturnQuantities = isWastage
    ? quantity > 0 && billData?.items?.[0] && !isItemExpired(billData.items[0]) && quantity <= (billData?.items?.[0]?.stockQty || 0)
    : billData?.items?.some(
        (item) =>
          (returnQuantities[item.id] || 0) > 0 &&
          !isItemExpired(item) &&
          (returnQuantities[item.id] || 0) <= item.stockQty
      );

  const makeAPICall = useCallback(
    async (url, options = {}) => {
      try {
        // console.log(`Making API call to: ${url}`, options);

        const response = await fetch(url, {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
            ...options.headers,
          },
          ...options,
        });

        // console.log(
        //   `API Response Status: ${response.status} ${response.statusText}`
        // );

        const responseText = await response.text();
        // console.log(`API Response Body: ${responseText.slice(0, 500)}...`);

        if (!response.ok) {
          let errorMessage;
          try {
            const errorData = JSON.parse(responseText);
            errorMessage =
              errorData.error || `HTTP ${response.status}: ${response.statusText}`;
          } catch {
            errorMessage = `HTTP ${response.status}: ${response.statusText} - ${responseText}`;
          }
          throw new Error(errorMessage);
        }

        return JSON.parse(responseText);
      } catch (error) {
        console.error(`API call failed for ${url}:`, error);
        throw error;
      }
    },
    [API_KEY]
  );

  // Helper function to get live stock from ALL purchases (in single units)
  const getLiveStockFromAllPurchases = useCallback(
    async (medicineId, medicineName) => { // Added async keyword
      let totalAvailableSingleUnits = 0;
      try {
        const allPurchasesResponse = await makeAPICall(
          `${API_BASE_URL}/api/medicine-purchase/all`,
          { method: "GET" }
        );

        const allPurchases = allPurchasesResponse.purchases || [];
        if (!Array.isArray(allPurchases)) {
          console.warn("API for all purchases did not return an array:", allPurchasesResponse);
          setError("স্টক ডেটা লোড করতে সমস্যা হয়েছে। কিছু তথ্য অসম্পূর্ণ হতে পারে।");
          return 0;
        }

        totalAvailableSingleUnits = allPurchases.reduce((sum, purchase) => {
          if (!purchase.items || !Array.isArray(purchase.items)) return sum;
          const itemQuantity = purchase.items.reduce((itemSum, item) => {
            if (
              (medicineId && item.medicineId === medicineId) ||
              (medicineName &&
                item.medicineName?.toLowerCase().trim() ===
                  medicineName.toLowerCase().trim())
            ) {
              // Assuming availableQuantity is ALREADY in single units in purchase.items
              return itemSum + (parseInt(item.availableQuantity) || 0); 
            }
            return itemSum;
          }, 0);
          return sum + itemQuantity;
        }, 0);
        return totalAvailableSingleUnits;
      } catch (purchaseFetchError) {
        console.error("Failed to fetch all purchase data for live stock calculation:", purchaseFetchError);
        setError("স্টক ডেটা লোড করতে সমস্যা হয়েছে। কিছু তথ্য অসম্পূর্ণ হতে পারে।");
        return 0;
      }
    },
    [makeAPICall, API_BASE_URL, setError] // Depend on makeAPICall and API_BASE_URL
  );

  const processReturnData = useCallback(
    async (returnData, sourceItems) => { // Made async to await getLiveStockFromAllPurchases
      // console.log("Processing return data:", { returnData, sourceItems });

      if (!returnData?.items || returnData.items.length === 0) {
        throw new Error("রিটার্ন ডেটাতে কোনো আইটেম পাওয়া যায়নি।");
      }

      // Use Promise.all to fetch live stock for all items concurrently
      const itemsForBillData = await Promise.all(sourceItems.map(async (sourceItem) => { // Use sourceItems here, not returnData.items
        const liveStockQty = await getLiveStockFromAllPurchases(
          sourceItem.medicineId || sourceItem.id, // Use medicineId or id for lookup
          sourceItem.medicineName
        );

        let itemData = {
          id: sourceItem.id || sourceItem.medicineId, // Ensure unique ID for table key
          medicineId: sourceItem.medicineId, // Ensure medicineId is correctly propagated
          medicineName: sourceItem.medicineName,
          expiryDate: sourceItem.expiryDate ? new Date(sourceItem.expiryDate).toISOString() : null,
          purchaseId: sourceItem.purchaseId || returnData.billId, // Keep original purchase ID
          stockQty: liveStockQty, // Use live stock for current available
          availableQuantity: liveStockQty, // Consistency
          tax: sourceItem.tax || 0,
          boxQty: sourceItem.boxQty || sourceItem.unitPerBox || 1, // Propagate boxQty/unitPerBox
          unitPerBox: sourceItem.unitPerBox || sourceItem.boxQty || 1, // Propagate unitPerBox

        };

        if (isCustomerReturn) {
          itemData = {
            ...itemData,
            quantity: sourceItem.quantity, // Original sold quantity from bill (in single units)
            salePrice: sourceItem.salePrice || 0,
            unitPrice: sourceItem.salePrice || 0,
          };
        } else if (isSupplierReturn) {
          const unitsPerBox = sourceItem.unitPerBox || sourceItem.boxQty || 1;
          const purchasedSingleUnits = sourceItem.quantity || 0; // This is the total single units purchased for this lot
          const unitPrice = sourceItem.unitPrice || (unitsPerBox > 0 ? (sourceItem.boxPrice || sourceItem.supplierPrice || 0) / unitsPerBox : (sourceItem.supplierPrice || 0));

          itemData = {
            ...itemData,
            quantity: purchasedSingleUnits, // Total single units purchased from original lot
            salePrice: unitPrice, // This is the unit price for a single item for display in the table
            supplierPrice: unitPrice, // For consistency, keep supplierPrice as unit price
            unitPrice: unitPrice, // Ensure unitPrice is set for calculations
            boxPrice: sourceItem.boxPrice || 0,
          };
        }

        return itemData;
      }));

      return itemsForBillData;
    },
    [isCustomerReturn, isSupplierReturn, getLiveStockFromAllPurchases] // Depend on getLiveStockFromAllPurchases
  );

const fetchReturn = useCallback(async () => {
  setLoading(true);
  setError("");
  setWarning("");
  setBillData(null);

  try {
    const returnId = parseInt(id);
    if (isNaN(returnId) || returnId <= 0) {
      throw new Error("অবৈধ রিটার্ন আইডি।");
    }

    if (!["CustomerReturn", "SupplierReturn", "Wastage"].includes(type)) {
      throw new Error("অবৈধ রিটার্ন টাইপ।");
    }

    // console.log(`Fetching return data for type: ${type}, id: ${id}`);

    const data = await makeAPICall(`${API_BASE_URL}/api/return/${endpoint}`, {
      method: "GET",
    });

    // console.log("Fetched return data:", JSON.stringify(data, null, 2));

    if (!data?.return) {
      throw new Error("কোনো রিটার্ন ডেটা পাওয়া যায়নি।");
    }

    const returnData = data.return;

    if (isWastage && returnData.adjustmentType !== "wastage") {
      throw new Error("এই রিটার্নটি ওয়াস্টেজ অ্যাডজাস্টমেন্ট নয়।");
    }

    setUpdatedAt(returnData.updatedAt || returnData.returnedAt);

    const parsedReturnDate = returnData.returnedAt
      ? new Date(returnData.returnedAt)
      : new Date();
    let returnDateFormatted;

    if (isNaN(parsedReturnDate.getTime())) {
      returnDateFormatted = getLocalDateString();
      console.warn("Invalid returnDate parsed, defaulting to current date.");
    } else {
      const year = parsedReturnDate.getFullYear();
      const month = String(parsedReturnDate.getMonth() + 1).padStart(2, "0");
      const day = String(parsedReturnDate.getDate()).padStart(2, "0");
      returnDateFormatted = `${year}-${month}-${day}`;
    }

    setReturnDate(returnDateFormatted);

    let originalBillOrPurchaseCreatedAt;
    if (isCustomerReturn && returnData.pharmacyBill?.createdAt) {
      originalBillOrPurchaseCreatedAt = normalizeDateToLocalDay(returnData.pharmacyBill.createdAt);
    } else if (isSupplierReturn && returnData.purchase?.createdAt) {
      originalBillOrPurchaseCreatedAt = normalizeDateToLocalDay(returnData.purchase.createdAt);
    } else {
      originalBillOrPurchaseCreatedAt = normalizeDateToLocalDay(new Date());
    }

    if (!originalBillOrPurchaseCreatedAt || isNaN(originalBillOrPurchaseCreatedAt.getTime())) {
      console.warn(
        "Invalid original Bill/Purchase createdAt date parsed/normalized, defaulting to current date (normalized)."
      );
      originalBillOrPurchaseCreatedAt = normalizeDateToLocalDay(new Date());
    }
    // console.log("Original bill/purchase created at (normalized):", originalBillOrPurchaseCreatedAt.toISOString());

    let sourceItems = [];
    if (isCustomerReturn && returnData.pharmacyBill?.items) {
      sourceItems = returnData.pharmacyBill.items;
    } else if (isSupplierReturn && returnData.purchase?.items) {
      try {
        sourceItems = Array.isArray(returnData.purchase.items)
          ? returnData.purchase.items
          : JSON.parse(returnData.purchase.items || "[]");
      } catch (parseError) {
        console.error("Error parsing purchase items:", parseError);
        sourceItems = [];
      }
    }

    // console.log("Source items from original bill/purchase:", sourceItems);

    if (isWastage && returnData.items && returnData.items.length > 0) {
      const wastageItem = returnData.items[0];
      const liveStockForWastage = await getLiveStockFromAllPurchases(
        wastageItem.medicineId || wastageItem.id,
        wastageItem.medicineName
      );

      setBillData({
        id: returnData.id || null,
        billNumber: returnData.billNumber || "",
        billId: returnData.billId || "",
        medicineName: wastageItem.medicineName || "অজানা",
        quantity: wastageItem.quantity || 0,
        salePrice: wastageItem.salePrice || 0,
        tax: parseFloat(wastageItem.tax || 0),
        totalAmount: parseFloat(wastageItem.total || 0),
        expiryDate: wastageItem.expiryDate || null,
        createdAt: originalBillOrPurchaseCreatedAt,
        items: [
          {
            id: wastageItem.itemId || wastageItem.id,
            medicineId: wastageItem.medicineId || wastageItem.id,
            medicineName: wastageItem.medicineName,
            quantity: wastageItem.quantity,
            salePrice: wastageItem.salePrice,
            tax: wastageItem.tax,
            stockQty: liveStockForWastage,
            expiryDate: wastageItem.expiryDate,
            purchaseId: returnData.billId,
            availableQuantity: liveStockForWastage,
            unitPrice: wastageItem.salePrice || 0,
            boxQty: wastageItem.boxQty || wastageItem.unitPerBox || 1,
            boxPrice: wastageItem.boxPrice || 0,
            unitPerBox: wastageItem.unitPerBox || wastageItem.boxQty || 1,
          },
        ],
      });

      setQuantity(wastageItem.quantity || 0);
      setNote(returnData.note || "");
      setAdjustmentType("wastage");
    } else {
      const itemsForBillData = await processReturnData(returnData, sourceItems);

      if (itemsForBillData.length === 0) {
        throw new Error("এই রিটার্ন ট্রানজ্যাকশনের জন্য কোনো বৈধ আইটেম পাওয়া যায়নি।");
      }

      const initialReturnQuantities = returnData.items.reduce((acc, returnedItemFromAPI) => {
        let itemToMatch = null;
        if (isCustomerReturn) {
          // Match using id or itemId if present
          itemToMatch = itemsForBillData.find(item => item.id === returnedItemFromAPI.id || item.id === returnedItemFromAPI.itemId);
        } else if (isSupplierReturn) {
          itemToMatch = itemsForBillData.find(item => item.id === returnedItemFromAPI.id || item.medicineId === returnedItemFromAPI.id);
        }

        if (itemToMatch) {
          acc[itemToMatch.id] = returnedItemFromAPI.returnQty || returnedItemFromAPI.quantity || 0;
        } else {
          console.warn(`No matching item found in processed data for returned item from API:`, returnedItemFromAPI);
        }
        return acc;
      }, {});

      // console.log("Initial return quantities (in single units):", initialReturnQuantities);
      setReturnQuantities(initialReturnQuantities);

      setBillData({
        billNumber: returnData.billNumber,
        billId: returnData.billId,
        createdAt: originalBillOrPurchaseCreatedAt,
        items: itemsForBillData,
        customerName: isCustomerReturn ? returnData.customerName : "",
        supplierName: isSupplierReturn ? returnData.customerName : "",
      });

      setCustomerName(isCustomerReturn ? returnData.customerName || "" : "");
      setSupplierName(isSupplierReturn ? returnData.customerName || "" : "");
      setAdjustmentType(returnData.adjustmentType || "stock");
      setNote(returnData.note || "");
    }
  } catch (err) {
    console.error("Error in fetchReturn:", err);
    setError(err.message || "রিটার্ন ডেটা লোড করতে ত্রুটি।");
  } finally {
    setLoading(false);
  }
}, [
  id,
  type,
  makeAPICall,
  getLocalDateString,
  isWastage,
  isCustomerReturn,
  isSupplierReturn,
  processReturnData,
  getLiveStockFromAllPurchases,
  endpoint,
  normalizeDateToLocalDay,
]);

  useEffect(() => {
    fetchReturn();
  }, [fetchReturn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setWarning("");

    try {
      if (!billData) {
        throw new Error(`কোনো ${isWastage ? "ওয়াস্টেজ" : "রিটার্ন"} ডেটা পাওয়া যায়নি।`);
      }

      if (!note.trim()) {
        throw new Error("নোট আবশ্যক।");
      }

      if (isCustomerReturn && !customerName.trim()) {
        throw new Error("গ্রাহকের নাম আবশ্যক।");
      }

      if (isSupplierReturn && !supplierName.trim()) {
        throw new Error("সরবরাহকারীর নাম আবশ্যক।");
      }

      if (!hasValidReturnQuantities) {
        throw new Error("অন্তত একটি অ-মেয়াদোত্তীর্ণ আইটেমের জন্য বৈধ রিটার্ন পরিমাণ প্রবেশ করুন।");
      }

      try {
        if (!returnDate) {
          throw new Error("রিটার্ন তারিখ আবশ্যক।");
        }

        const [year, month, day] = returnDate.split("-").map(Number);
        const selectedLocalDate = new Date(year, month - 1, day);

        if (isNaN(selectedLocalDate.getTime())) {
          throw new Error("অবৈধ রিটার্ন তারিখ ফরম্যাট।");
        }

        let billCreatedAtDate = null;
        if (billData.createdAt instanceof Date && !isNaN(billData.createdAt.getTime())) {
          billCreatedAtDate = billData.createdAt;
        } else if (typeof billData.createdAt === "string") {
          billCreatedAtDate = new Date(billData.createdAt);
          if (isNaN(billCreatedAtDate.getTime())) {
            billCreatedAtDate = null;
          }
        }
        // billCreatedAtDate should already be normalized to local day from fetchReturn

        if (billCreatedAtDate) {
          validateReturnDateLocal(selectedLocalDate, billCreatedAtDate, "বিল/ক্রয়");
        } else {
          console.warn("billData.createdAt is not a valid Date object or parseable string for validation.");
        }
      } catch (dateError) {
        console.error("Local date validation error:", dateError);
        throw new Error(dateError.message || "তারিখ যাচাইকরণে সমস্যা।");
      }

      const processedReturnItems = billData.items
        .map((billItem) => {
          const quantityToReturn = isWastage
            ? parseInt(quantity)
            : returnQuantities[billItem.id];

          if (quantityToReturn > 0 && !isItemExpired(billItem)) {
            return {
              itemId: billItem.id,
              quantity: parseInt(quantityToReturn), // Ensure it's an integer
              unitPrice: billItem.unitPrice || billItem.salePrice, // Use unitPrice if available, fallback to salePrice
              tax: billItem.tax,
            };
          }
          return null;
        })
        .filter((item) => item !== null);

      // console.log("Processed return items (for API payload):", processedReturnItems);

      if (processedReturnItems.length === 0) {
        throw new Error("জমা দেওয়ার জন্য কোনো বৈধ রিটার্ন আইটেম নেই।");
      }

      // Recalculate totals based on processedReturnItems (which should contain unitPrice and tax)
      const totalTaxRecalculated = processedReturnItems
        .reduce((sum, processedItem) => {
          const itemTaxAmount =
            processedItem.quantity * processedItem.unitPrice * ((processedItem.tax || 0) / 100);
          return sum + itemTaxAmount;
        }, 0)
        .toFixed(2);

      const netReturnRecalculated = processedReturnItems
        .reduce((sum, processedItem) => {
          const itemTotal =
            processedItem.quantity * processedItem.unitPrice * (1 + (processedItem.tax || 0) / 100);
          return sum + itemTotal;
        }, 0)
        .toFixed(2);

      const payload = {
        adjustmentType,
        totalDeduction: "0.00",
        totalTax: totalTaxRecalculated,
        netReturn: netReturnRecalculated,
        note: note.trim(),
        returnItems: processedReturnItems, // This contains single units
        customerName: isSupplierReturn ? supplierName : customerName,
        returnedAt: getLocalDateTimeISO(returnDate),
      };

      if (isSupplierReturn) {
        payload.purchaseId = billData.billId;
        payload.billNumber = billData.billNumber;
      } else if (isWastage) {
        payload.billId = billData.billId;
        payload.billNumber = billData.billNumber;
        payload.returnItems = [{
            itemId: billData.items[0].id, // Medicine ID of the wasted item (pharmacyBillItem ID or medicineId)
            quantity: parseInt(quantity), // The single quantity from state
            medicineName: billData.items[0].medicineName,
            unitPrice: billData.items[0].unitPrice || billData.items[0].salePrice, // Use the relevant price
            tax: billData.items[0].tax,
        }];
        payload.totalTax = (payload.returnItems[0].quantity * payload.returnItems[0].unitPrice * (payload.returnItems[0].tax / 100)).toFixed(2);
        payload.netReturn = (payload.returnItems[0].quantity * payload.returnItems[0].unitPrice * (1 + (payload.returnItems[0].tax / 100))).toFixed(2);

      } else if (isCustomerReturn) {
        payload.billNumber = billData.billNumber;
      }

      // console.log("Submitting payload:", JSON.stringify(payload, null, 2));

      const result = await makeAPICall(`${API_BASE_URL}/api/return/${endpoint}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      // console.log("Update response:", result);

      toast.success(`${isWastage ? "ওয়াস্টেজ" : type.replace("Return", "")} সফলভাবে আপডেট করা হয়েছে`, {
        position: "top-right",
        autoClose: 3000,
      });

      navigate(navigateTo);
    } catch (err) {
      console.error("Error in handleSubmit:", err);
      let errorMessage = err.message;

      if (errorMessage.includes("Invalid return item")) {
        errorMessage = `অবৈধ আইটেম ডেটা: ${errorMessage}। নিশ্চিত করুন সব আইটেমে বৈধ itemId আছে।`;
      } else if (errorMessage.includes("not found")) {
        errorMessage = `রিটার্ন রেকর্ডটি আর উপলব্ধ নয়। এটি মুছে ফেলা হয়ে থাকতে পারে।`;
      } else if (errorMessage.includes("Concurrent modification")) {
        errorMessage = "রিটার্ন রেকর্ডটি অন্য ব্যবহারকারী দ্বারা পরিবর্তিত হয়েছে।";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    navigate(navigateTo);
  }, [navigate, navigateTo]);

  if (loading) {
    return (
      <div className="p-4">
        <SkeletonTheme baseColor="#e5e7eb" highlightColor="#f3f4f6">
          <Skeleton height={40} width="50%" className="mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Skeleton height={40} />
            <Skeleton height={40} />
          </div>
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th>
                  <Skeleton height={20} width="80%" />
                </th>
                <th>
                  <Skeleton height={20} width="80%" />
                </th>
                <th>
                  <Skeleton height={20} width="80%" />
                </th>
                <th>
                  <Skeleton height={20} width="80%" />
                </th>
              </tr>
            </thead>
            <tbody>
              <TableSkeleton
                rowCount={isWastage ? 1 : 3}
                columns={[
                  { width: "40%", height: 30, className: "text-left" },
                  { width: "20%", height: 30, className: "text-center" },
                  { width: "20%", height: 30, className: "text-center" },
                  { width: "20%", height: 30, className: "text-center" },
                ]}
              />
            </tbody>
          </table>
          <Skeleton height={80} className="my-4" />
          <div className="flex gap-4">
            <Skeleton height={40} width={100} />
            <Skeleton height={40} width={100} />
          </div>
        </SkeletonTheme>
      </div>
    );
  }

  if (error || !billData) {
    return (
      <div className="text-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-600 font-medium">
            {error || `কোনো ${isWastage ? "ওয়াস্টেজ" : "রিটার্ন"} ডেটা পাওয়া যায়নি।`}
          </p>
          {warning && <p className="text-yellow-600 mt-2">{warning}</p>}
        </div>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition duration-200"
          onClick={() => navigate(navigateTo)}
        >
          {isWastage ? "ওয়াস্টেজ তালিকায় ফিরুন" : "রিটার্ন তালিকায় ফিরুন"}
        </button>
      </div>
    );
  }

  return (
    <div>
      {warning && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-yellow-700">{warning}</p>
        </div>
      )}

      <ReturnForm
        billData={billData}
        customerName={customerName}
        supplierName={supplierName}
        returnQuantities={
          isWastage ? { [billData.items[0].id]: quantity } : returnQuantities
        }
        note={note}
        adjustmentType={isWastage ? "wastage" : adjustmentType}
        returnDate={returnDate}
        setCustomerName={setCustomerName}
        setSupplierName={setSupplierName}
        setReturnQuantities={
          isWastage
            ? (newQuantities) => setQuantity(newQuantities[billData.items[0].id] || 0)
            : setReturnQuantities
        }
        setNote={setNote}
        setAdjustmentType={isWastage ? () => {} : setAdjustmentType}
        setReturnDate={setReturnDate}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isCustomerReturn={isCustomerReturn}
        isSupplierReturn={isSupplierReturn}
        loading={loading}
        error={error}
        setError={setError}
        successMessage=""
        isItemExpired={isItemExpired}
        hasValidReturnQuantities={hasValidReturnQuantities}
        isEditMode={true}
        getLocalDateTimeISO={getLocalDateTimeISO}
      />
    </div>
  );
};

export default EditReturn;